/**
 * Compact display for gendered word pairs, e.g. Meister/in or Meister/innen.
 */
export function pairLabel (masc, fem) {
  if (!masc && !fem) return ''
  if (!fem) return masc
  if (!masc) return fem
  if (fem.indexOf(masc) === 0 && fem.length > masc.length) {
    return `${masc}/${fem.slice(masc.length)}`
  }
  const stem = masc.slice(0, -1)
  if (stem && fem.indexOf(stem) === 0) {
    return `${masc}/in`
  }
  return `${masc} · ${fem}`
}

/** Pairform wording: „Feminin bzw. Maskulin“ / „Feminin und Maskulin“. */
export function pairFormText (masc, fem, numerus) {
  if (!masc || !fem) return ''
  if (numerus === 'pl' || numerus === 'Pl') {
    return `${fem} und ${masc}`
  }
  return `${fem} bzw. ${masc}`
}

export function genderedDisplay (entry) {
  if (!entry) {
    return {
      headline: '',
      subline: '',
      headlineLink: '',
      sublineLink: '',
      candidates: []
    }
  }

  const hasNounForms = !!(
    entry.singular_masculine || entry.singular_feminine ||
    entry.plural_masculine || entry.plural_feminine
  )

  const candidates = [
    entry.term,
    entry.singular_masculine,
    entry.singular_feminine,
    entry.plural_masculine,
    entry.plural_feminine
  ].filter(Boolean)

  if (entry.term && !hasNounForms) {
    return {
      headline: entry.term,
      subline: '',
      headlineLink: entry.term,
      sublineLink: '',
      candidates
    }
  }

  const singular = pairLabel(entry.singular_masculine, entry.singular_feminine)
  const plural = pairLabel(entry.plural_masculine, entry.plural_feminine)
  const headline = singular || entry.term || plural
  const subline = plural && plural !== singular ? plural : ''

  return {
    headline,
    subline,
    headlineLink: entry.singular_masculine || entry.term || entry.plural_masculine || '',
    sublineLink: entry.plural_masculine || entry.plural_feminine || '',
    candidates
  }
}

/**
 * Label + search term for Helfen thank-you chips (masc · fem, link to dict search).
 */
export function genderedSearchChip (entry) {
  if (!entry) return { label: '', search: '' }

  const joinForms = (masc, fem) => [masc, fem].filter(Boolean).join(' · ')
  const hasNounForms = !!(
    entry.singular_masculine || entry.singular_feminine ||
    entry.plural_masculine || entry.plural_feminine
  )

  if (!hasNounForms) {
    const term = entry.term || ''
    return { label: term, search: term }
  }

  let label = joinForms(entry.singular_masculine, entry.singular_feminine)
  let search = entry.singular_masculine || entry.singular_feminine || ''

  if (!label) {
    label = joinForms(entry.plural_masculine, entry.plural_feminine)
    search = entry.plural_masculine || entry.plural_feminine || ''
  }

  if (!search) {
    search = entry.term || label.split(' · ')[0] || ''
  }

  return { label, search }
}

/**
 * Individual gendered forms as pills (sg masc, sg fem, pl masc, pl fem).
 */
export function genderedPills (entry) {
  if (!entry) return []

  const pills = []
  const push = (text, link, candidates) => {
    if (!text) return
    pills.push({
      text,
      link: link || text,
      candidates: candidates.filter(Boolean)
    })
  }

  const hasNounForms = !!(
    entry.singular_masculine || entry.singular_feminine ||
    entry.plural_masculine || entry.plural_feminine
  )

  if (!hasNounForms) {
    if (entry.term) {
      push(entry.term, entry.term, [entry.term, entry.alternative])
    }
    return pills
  }

  push(
    entry.singular_masculine,
    entry.singular_masculine,
    [entry.singular_masculine, entry.singular_gender_neutral]
  )
  push(
    entry.singular_feminine,
    entry.singular_feminine,
    [entry.singular_feminine, entry.singular_gender_neutral]
  )
  push(
    entry.plural_masculine,
    entry.plural_masculine,
    [entry.plural_masculine, entry.plural_gender_neutral]
  )
  push(
    entry.plural_feminine,
    entry.plural_feminine,
    [entry.plural_feminine, entry.plural_gender_neutral]
  )

  return pills
}

const NOTATION_MARKERS = ['*', ':', '_', '/-', '/']

export function isNotationValue (value) {
  if (!value) return false
  return NOTATION_MARKERS.some(marker => value.includes(marker))
}

/**
 * Group gendered forms with their neutral alternative for card mapping rows.
 */
export function genderedMappingRows (entry) {
  if (!entry) return []

  const rows = []
  const hasNounForms = !!(
    entry.singular_masculine || entry.singular_feminine ||
    entry.plural_masculine || entry.plural_feminine
  )

  const pushPill = (pills, text, link, candidates, gender = '', number = '') => {
    if (!text) return
    pills.push({
      text,
      link: link || text,
      candidates: candidates.filter(Boolean),
      gender,
      number
    })
  }

  const pushRow = (key, icon, genderedWords, neutralText, candidates) => {
    if (!neutralText || !genderedWords.length) return
    rows.push({
      key,
      icon,
      genderedWords,
      alternativeText: neutralText,
      candidates: candidates.filter(Boolean),
      isNotation: false
    })
  }

  if (!hasNounForms) {
    if (entry.alternative && !isNotationValue(entry.alternative)) {
      const words = []
      pushPill(words, entry.term, entry.term, [entry.term, entry.alternative])
      pushRow(
        'term',
        'fas fa-circle fa-xs',
        words,
        entry.alternative,
        [entry.term, entry.alternative]
      )
    }
    return rows
  }

  if (entry.singular_gender_neutral && !isNotationValue(entry.singular_gender_neutral)) {
    const words = []
    pushPill(
      words,
      entry.singular_masculine,
      entry.singular_masculine,
      [entry.singular_masculine, entry.singular_gender_neutral],
      'masculine',
      'singular'
    )
    pushPill(
      words,
      entry.singular_feminine,
      entry.singular_feminine,
      [entry.singular_feminine, entry.singular_gender_neutral],
      'feminine',
      'singular'
    )
    pushRow(
      'singular',
      'fas fa-user fa-xs',
      words,
      entry.singular_gender_neutral,
      [entry.singular_masculine, entry.singular_feminine, entry.singular_gender_neutral]
    )
  }

  if (entry.plural_gender_neutral && !isNotationValue(entry.plural_gender_neutral)) {
    const words = []
    pushPill(
      words,
      entry.plural_masculine,
      entry.plural_masculine,
      [entry.plural_masculine, entry.plural_gender_neutral],
      'masculine',
      'plural'
    )
    pushPill(
      words,
      entry.plural_feminine,
      entry.plural_feminine,
      [entry.plural_feminine, entry.plural_gender_neutral],
      'feminine',
      'plural'
    )
    pushRow(
      'plural',
      'fas fa-user-friends fa-xs',
      words,
      entry.plural_gender_neutral,
      [entry.plural_masculine, entry.plural_feminine, entry.plural_gender_neutral]
    )
  }

  if (entry.alternative && !isNotationValue(entry.alternative)) {
    const words = []
    pushPill(words, entry.term, entry.term, [entry.term, entry.alternative])
    pushRow(
      'alternative',
      'fas fa-circle fa-xs',
      words,
      entry.alternative,
      [entry.term, entry.alternative]
    )
  }

  return rows
}

/**
 * Gendered source rows for Helfen — left column only, no neutral alternative required.
 */
export function genderedSourceRows (entry) {
  if (!entry) return []

  const rows = []
  const hasNounForms = !!(
    entry.singular_masculine || entry.singular_feminine ||
    entry.plural_masculine || entry.plural_feminine
  )

  const pushWord = (words, text, link, candidates) => {
    if (!text) return
    words.push({
      text,
      link: link || text,
      candidates: candidates.filter(Boolean)
    })
  }

  const pushRow = (key, icon, genderedWords, inputType) => {
    if (!genderedWords.length) return
    rows.push({
      key,
      icon,
      genderedWords,
      inputType,
      candidates: genderedWords.flatMap(word => word.candidates)
    })
  }

  if (!hasNounForms) {
    if (entry.term) {
      const words = []
      pushWord(words, entry.term, entry.term, [entry.term])
      pushRow('term', 'fas fa-circle fa-xs', words, 'term')
    }
    return rows
  }

  const wordsFor = (masc, fem) => {
    const words = []
    pushWord(words, masc, masc, [masc])
    pushWord(words, fem, fem, [fem])
    return words
  }

  if (entry.singular_masculine || entry.singular_feminine) {
    pushRow(
      'singular',
      'fas fa-user fa-xs',
      wordsFor(entry.singular_masculine, entry.singular_feminine),
      'singular'
    )
  }

  if (entry.plural_masculine || entry.plural_feminine) {
    pushRow(
      'plural',
      'fas fa-user-friends fa-xs',
      wordsFor(entry.plural_masculine, entry.plural_feminine),
      'plural'
    )
  }

  return rows
}

/**
 * Mapping rows for star/pair notation entries (gendered left, notation right).
 */
export function notationMappingRows (entry, placeholderChar = '*') {
  if (!entry) return []

  const isPair = entry.type === 'pair'
  const rows = []

  const pushWord = (words, text, link, candidates, gender = '', number = '') => {
    if (!text) return
    words.push({
      text,
      link: link || text,
      candidates: candidates.filter(Boolean),
      gender,
      number
    })
  }

  const genderedWordsFor = (masc, fem, number, extraCandidates = []) => {
    const words = []
    pushWord(words, masc, masc, [masc, ...extraCandidates], 'masculine', number)
    pushWord(words, fem, fem, [fem, ...extraCandidates], 'feminine', number)
    return words
  }

  const formatNotation = (text) => {
    if (!text || isPair) return text
    return text.replace(/\*/g, placeholderChar)
  }

  const pushRow = (key, icon, genderedWords, alternativeText, candidates) => {
    if (!alternativeText || !genderedWords.length) return
    rows.push({
      key,
      icon,
      genderedWords,
      alternativeText,
      candidates: candidates.filter(Boolean),
      isNotation: true
    })
  }

  if (entry.alternative && (isPair || isNotationValue(entry.alternative))) {
    const words = []
    pushWord(words, entry.term, entry.term, [entry.term, entry.alternative])
    pushRow(
      'alternative',
      'fas fa-circle fa-xs',
      words,
      formatNotation(entry.alternative),
      [entry.term, entry.alternative]
    )
  }

  let singularText = entry.singular_gender_neutral
  let pluralText = entry.plural_gender_neutral
  if (isPair) {
    if (!singularText) {
      singularText = pairFormText(entry.singular_masculine, entry.singular_feminine, 'sg')
    }
    if (!pluralText) {
      pluralText = pairFormText(entry.plural_masculine, entry.plural_feminine, 'pl')
    }
  }

  if (singularText && (isPair || isNotationValue(singularText))) {
    const words = genderedWordsFor(
      entry.singular_masculine,
      entry.singular_feminine,
      'singular',
      [singularText]
    )
    if (words.length) {
      pushRow(
        'singular',
        'fas fa-user fa-xs',
        words,
        formatNotation(singularText),
        [entry.singular_masculine, entry.singular_feminine, singularText]
      )
    }
  }

  if (pluralText && (isPair || isNotationValue(pluralText))) {
    const words = genderedWordsFor(
      entry.plural_masculine,
      entry.plural_feminine,
      'plural',
      [pluralText]
    )
    if (words.length) {
      pushRow(
        'plural',
        'fas fa-user-friends fa-xs',
        words,
        formatNotation(pluralText),
        [entry.plural_masculine, entry.plural_feminine, pluralText]
      )
    }
  }

  return rows
}

/** Unified mapping rows for neut, star, and pair dictionary cards. */
export function entryMappingRows (entry, options = {}) {
  if (!entry) return []
  if (entry.type === 'star' || entry.type === 'pair') {
    return notationMappingRows(entry, options.placeholderChar || '*')
  }
  return genderedMappingRows(entry)
}

function normalizedSearch (value) {
  return String(value || '').trim().toLocaleLowerCase()
}

/**
 * Choose one source form and its matching alternative for the collapsed table row.
 * Search matches win; otherwise prefer masculine plural, then the next available form.
 */
export function collapsedMappingRow (rows, searchTerm = '') {
  if (!rows || !rows.length) return null

  const sources = rows.flatMap(row =>
    row.genderedWords.map(word => ({ row, word }))
  )
  if (!sources.length) return rows[0]

  const search = normalizedSearch(searchTerm)
  let selected

  if (search) {
    selected = sources.find(({ word }) => normalizedSearch(word.text) === search)
    if (!selected) {
      selected = sources.find(({ word }) => normalizedSearch(word.text).includes(search))
    }
    if (!selected) {
      const matchingRow = rows.find(row => normalizedSearch(row.alternativeText) === search) ||
        rows.find(row => normalizedSearch(row.alternativeText).includes(search))
      if (matchingRow) {
        selected = sources.find(source => source.row === matchingRow)
      }
    }
  }

  const priority = [
    ['masculine', 'plural'],
    ['feminine', 'plural'],
    ['masculine', 'singular'],
    ['feminine', 'singular']
  ]
  if (!selected) {
    selected = priority
      .map(([gender, number]) => sources.find(({ word }) => word.gender === gender && word.number === number))
      .find(Boolean)
  }
  if (!selected) selected = sources[0]

  return {
    ...selected.row,
    genderedWords: [selected.word]
  }
}
